<template>
  <div class="grid">
    <div class="col-12">
      <div
        class="flex flex-column md:flex-row md:justify-content-between md:align-items-end gap-3"
      >
        <div>
          <h1 class="text-3xl font-semibold text-900 m-0">
            Management Dashboard
          </h1>
        </div>
        <div class="flex flex-column sm:flex-row gap-3 align-items-end">
          <div class="flex flex-column gap-1">
            <label
              for="timezone-select"
              class="text-xs font-semibold text-600 uppercase"
            >
              Timezone
            </label>
            <Dropdown
              input-id="timezone-select"
              v-model="timezone"
              :options="timezoneOptions"
              option-label="label"
              option-value="value"
              class="w-full md:w-16rem"
              :disabled="isRefreshing"
            />
          </div>
          <Button
            icon="pi pi-refresh"
            label="Refresh"
            :loading="isRefreshing"
            class="md:w-auto"
            @click="refreshAll"
          />
        </div>
      </div>
    </div>

    <div class="col-12">
      <h2 class="md-section-heading">Overall Activity</h2>
    </div>
    <div class="col-12" v-if="overallStatCards.length">
      <SummaryStatCards
        id-prefix="overall-summary"
        :cards="overallStatCards"
        :loading="overallSummaryLoading"
        :placeholder-labels="overallCardLabels"
      />
    </div>
    <div class="col-12">
      <TrendChart
        title="Overall Trend Comparison"
        :metric-label="overallTrendMetricLabel"
        :datasets="overallTrendDatasets"
        :loading="overallTrendLoading"
        :error="overallTrendError"
        :metric-options="trendMetricOptions"
        :selected-metric="selectedOverallMetric"
        :period-options="trendPeriodOptions"
        :selected-period="selectedOverallPeriod"
        :value-prefix="overallChartPrefix"
        :value-suffix="overallChartSuffix"
        @update:metric="handleOverallMetricChange"
        @update:period="handleOverallPeriodChange"
      />
    </div>

    <div class="col-12">
      <h2 class="md-section-heading">Publisher Network</h2>
    </div>
    <div class="col-12">
      <SummaryStatCards
        :cards="publisherStatCards"
        :loading="publisherSummaryLoading"
        :placeholder-labels="publisherCardSpec.map(({ label }) => label)"
      />
    </div>
    <div class="col-12">
      <TrendChart
        title="Publisher Trend Comparison"
        :metric-label="trendMetricLabel"
        :datasets="publisherTrendDatasets"
        :loading="publisherTrendLoading"
        :error="publisherTrendError"
        :metric-options="trendMetricOptions"
        :selected-metric="selectedTrendMetric"
        :period-options="trendPeriodOptions"
        :selected-period="selectedTrendPeriod"
        :value-prefix="trendChartPrefix"
        :value-suffix="trendChartSuffix"
        @update:metric="handleTrendMetricChange"
        @update:period="handleTrendPeriodChange"
      />
    </div>
    <div class="col-12">
      <PublisherSnapshotPanels
        :month-cards="publisherMonthCards"
        :year-cards="publisherYearCards"
        :month-loading="publisherMonthBusy"
        :year-loading="publisherYearBusy"
        :month-placeholder-labels="
          publisherSnapshotSpec.map(({ label }) => label)
        "
        :year-placeholder-labels="
          publisherSnapshotSpec.map(({ label }) => label)
        "
      />
    </div>

    <div class="col-12">
      <NetworkSection
        title="Publisher Details"
        :summary-cards="[]"
        :summary-loading="false"
        summary-loading-message=""
        summary-empty-message=""
        summary-id-prefix="publisher-summary"
        :metric-sections="[]"
        :run-rate="publisherRunRateSection"
        :tables="publisherTables"
      />
    </div>

    <div class="col-12 mt-5">
      <h2 class="md-section-heading">Podcast Network</h2>
    </div>
    <div class="col-12">
      <SummaryStatCards
        :cards="podcastStatCards"
        :loading="podcastSummaryLoading"
        :placeholder-labels="podcastCardSpec.map(({ label }) => label)"
      />
    </div>
    <div class="col-12">
      <MetricCardSection
        id-prefix="podcast-month"
        title="Month To Date"
        :cards="podcastMonthCards"
        :loading="podcastMonthBusy"
        loading-message="Loading month-to-date metrics..."
        empty-message="No month-to-date metrics."
        :placeholder-labels="podcastSnapshotSpec.map(({ label }) => label)"
      />
    </div>
    <!--
    <div class="col-12">
      <MetricCardSection
        id-prefix="podcast-year"
        title="Year To Date"
        :cards="podcastYearCards"
        :loading="podcastYearBusy"
        loading-message="Loading year-to-date metrics..."
        empty-message="No year-to-date metrics."
        :placeholder-labels="podcastSnapshotSpec.map(({ label }) => label)"
      />
    </div>
    -->
    <div class="col-12">
      <NetworkSection
        title="Podcast Details"
        :summary-cards="[]"
        :summary-loading="false"
        summary-loading-message=""
        summary-empty-message=""
        summary-id-prefix="podcast-summary"
        :metric-sections="podcastDetailSections"
        :tables="podcastTables"
      />
    </div>
  </div>

  <Dialog
    v-model:visible="revenueDialogState.visible"
    modal
    header="Edit Planned Monthly Revenue"
    :style="{ width: '28rem' }"
    :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
  >
    <div class="flex flex-column gap-3">
      <div class="flex flex-column gap-1">
        <span class="text-sm font-semibold text-700">
          {{ currentMonthLabel }}
        </span>
        <span class="text-xs text-500">
          Set the planned revenue for the month.
        </span>
      </div>
      <InputNumber
        v-model="revenueDialogState.value"
        mode="currency"
        currency="USD"
        locale="en-US"
        :min="0"
        :use-grouping="true"
        class="w-full"
        input-class="w-full"
      />
      <small v-if="revenueDialogState.error" class="text-xs text-red-500">
        {{ revenueDialogState.error }}
      </small>
    </div>
    <template #footer>
      <div class="flex justify-content-end gap-2 w-full">
        <Button
          label="Cancel"
          severity="secondary"
          outlined
          @click="closeRevenueDialog"
        />
        <Button
          label="Save"
          :loading="revenueDialogState.saving"
          @click="submitRevenueUpdate"
        />
      </div>
    </template>
  </Dialog>

  <Dialog
    v-model:visible="loadsDialogState.visible"
    modal
    header="Edit Planned Monthly Loads"
    :style="{ width: '28rem' }"
    :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
  >
    <div class="flex flex-column gap-3">
      <div class="flex flex-column gap-1">
        <span class="text-sm font-semibold text-700">
          {{ currentMonthLabel }}
        </span>
        <span class="text-xs text-500">
          Set the planned loads for the month.
        </span>
      </div>
      <InputNumber
        v-model="loadsDialogState.value"
        mode="decimal"
        :min="0"
        :use-grouping="true"
        :max-fraction-digits="2"
        class="w-full"
        input-class="w-full"
      />
      <small v-if="loadsDialogState.error" class="text-xs text-red-500">
        {{ loadsDialogState.error }}
      </small>
    </div>
    <template #footer>
      <div class="flex justify-content-end gap-2 w-full">
        <Button
          label="Cancel"
          severity="secondary"
          outlined
          @click="closeLoadsDialog"
        />
        <Button
          label="Save"
          :loading="loadsDialogState.saving"
          @click="submitLoadsUpdate"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { computed, reactive, ref, watch } from "vue";
import axios from "axios";
import Dropdown from "primevue/dropdown";
import Button from "primevue/button";
import InputNumber from "primevue/inputnumber";
import { useToast } from "primevue/usetoast";
import NetworkSection from "./components/NetworkSection.vue";
import SummaryStatCards from "./components/SummaryStatCards.vue";
import MetricCardSection from "./components/MetricCardSection.vue";
import PublisherSnapshotPanels from "./components/PublisherSnapshotPanels.vue";
import TrendChart from "./components/TrendChart.vue";
import { formatMoney } from "@/helpers";
import { API_BASE_URL } from "@/consts";

const toast = useToast();

const timezoneOptions = [
  { label: "UTC", value: "UTC" },
  { label: "Israel (Asia/Jerusalem)", value: "Asia/Jerusalem" },
];

const periods = ["today", "yesterday", "monthToDate", "yearToDate"];

const publisherPeriods = periods;
// Temporarily exclude year-to-date for podcast metrics due to failing query (ECONNRESET).
const podcastPeriods = ["today", "yesterday", "monthToDate"]; // comment to re-enable: add "yearToDate"

const timezone = ref("Asia/Jerusalem");

const tablePeriodOptions = [
  { label: "Last 24 Hours", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 30 Days", value: "last30Days" },
  { label: "Month To Date", value: "monthToDate" },
  { label: "Year To Date", value: "yearToDate" },
];

const trendMetricOptions = [
  { label: "Total Revenue", value: "revenue", prefix: "$" },
  { label: "Total Profit", value: "profit", prefix: "$" },
  { label: "Avg Profit Margin", value: "profitMargin", suffix: "%" },
];

const trendPeriodOptions = [
  { label: "Today vs Yesterday", value: "today" },
  { label: "Last 30 Days", value: "last30Days" },
  { label: "This Month vs Previous Month", value: "monthToDate" },
  { label: "This Year vs Last Year", value: "year" },
];

const selectedTrendMetric = ref(trendMetricOptions[0].value);
const selectedTrendPeriod = ref(trendPeriodOptions[0].value);
const selectedOverallMetric = ref(trendMetricOptions[0].value);
const selectedOverallPeriod = ref(trendPeriodOptions[0].value);

// Vue 3 Composition API - add this to your ManagementDashboard component

const buildTrendDatasets = (trendSource, selectedMetricRef) =>
  computed(() => {
    if (!trendSource.current.length && !trendSource.comparison.length) {
      return [];
    }

    const granularity = trendSource.granularity || "hour";
    const metric = selectedMetricRef.value;

    const parseTimestamp = (value) => {
      const date = new Date(value);
      if (!value || Number.isNaN(date.getTime())) return null;
      return date;
    };

    const sortPoints = (collection) => {
      const items = Array.isArray(collection) ? [...collection] : [];
      const getSortValue = (point) => {
        if (point?.timestamp) {
          const parsed = parseTimestamp(point.timestamp);
          if (parsed) return parsed.getTime();
        }
        if (Number.isFinite(point?.bucketIndex)) return point.bucketIndex;
        if (Number.isFinite(point?.dayOfMonth)) return point.dayOfMonth;
        if (Number.isFinite(point?.monthOfYear)) return point.monthOfYear;
        return 0;
      };
      return items.sort((a, b) => getSortValue(a) - getSortValue(b));
    };

    const roundValue = (value) => {
      if (!Number.isFinite(value)) return 0;
      return Math.round(value * 100) / 100;
    };

    const MONTH_LABELS = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    // Format label based on granularity
    // For day and month: use data already converted by backend
    // For hour: convert timestamp to local timezone for display
    const formatLabel = (point, index) => {
      if (granularity === "hour") {
        // For hours: use timestamp and convert to selected timezone for display
        const date = parseTimestamp(point.timestamp);
        if (!date) return `#${index + 1}`;

        const formatter = new Intl.DateTimeFormat("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
          timeZone: timezone.value,
        });
        return formatter.format(date);
      }

      if (granularity === "day") {
        // For days: check if we need to show month + day (for cross-month periods like last30days)
        // or just day (for single series or month comparisons)

        // If we have comparison data, we're comparing two periods (e.g., current vs previous month)
        // In this case, show just the day number for alignment
        const hasComparison =
          trendSource.comparison && trendSource.comparison.length > 0;

        if (hasComparison) {
          // Month comparison: show just day numbers (01, 02, 03...)
          if (Number.isFinite(point?.dayOfMonth)) {
            return String(point.dayOfMonth).padStart(2, "0");
          }
          return String(index + 1).padStart(2, "0");
        }

        // Single series (like last30days): check if it spans multiple months
        const date = parseTimestamp(point.timestamp);

        if (!date) {
          // Fallback to dayOfMonth if no timestamp
          if (Number.isFinite(point?.dayOfMonth)) {
            return String(point.dayOfMonth).padStart(2, "0");
          }
          return String(index + 1).padStart(2, "0");
        }

        // Check if current series spans multiple months
        const currentPoints = sortPoints(trendSource.current);
        const months = new Set();
        currentPoints.forEach((p) => {
          const d = parseTimestamp(p.timestamp);
          if (d) {
            const localDate = new Date(
              d.toLocaleString("en-US", { timeZone: timezone.value })
            );
            months.add(localDate.getMonth());
          }
        });

        const isCrossMonth = months.size > 1;

        if (isCrossMonth) {
          // Show "Nov 09", "Nov 10", ..., "Dec 08"
          const formatter = new Intl.DateTimeFormat("en-US", {
            month: "short",
            day: "2-digit",
            timeZone: timezone.value,
          });
          return formatter.format(date);
        } else {
          // Show just "01", "02", ... "30"
          if (Number.isFinite(point?.dayOfMonth)) {
            return String(point.dayOfMonth).padStart(2, "0");
          }
          const localDate = new Date(
            date.toLocaleString("en-US", { timeZone: timezone.value })
          );
          return String(localDate.getDate()).padStart(2, "0");
        }
      }

      if (granularity === "month") {
        // For months: backend already provided monthOfYear in correct timezone
        // Just use it directly for the label
        if (Number.isFinite(point?.monthOfYear)) {
          return MONTH_LABELS[point.monthOfYear] || `#${point.monthOfYear + 1}`;
        }
        // Fallback to month labels by index
        return MONTH_LABELS[index] || `#${index + 1}`;
      }

      // Fallback for unknown granularity
      return `#${index + 1}`;
    };

    const transformValue = (value) => {
      const numeric = Number(value ?? 0);
      if (!Number.isFinite(numeric)) return 0;
      // Convert profit margin to percentage
      const scaled = metric === "profitMargin" ? numeric * 100 : numeric;
      return roundValue(scaled);
    };

    const toSeries = (collection) => {
      const dataset = [];
      sortPoints(collection).forEach((point, index) => {
        const label = formatLabel(point, index);
        if (!label) return;
        dataset.push([label, transformValue(point?.[metric])]);
      });
      return dataset;
    };

    const datasets = [];
    if (trendSource.current.length) {
      datasets.push({
        name: trendSource.currentLabel || "Current",
        data: toSeries(trendSource.current),
      });
    }
    if (trendSource.comparison.length) {
      datasets.push({
        name: trendSource.comparisonLabel || "Previous",
        data: toSeries(trendSource.comparison),
      });
    }
    return datasets;
  });

const publisher = reactive({
  snapshots: {
    today: null,
    yesterday: null,
    monthToDate: null,
    yearToDate: null,
  },
  generatedAt: {},
  context: {},
  pending: 0,
});

const podcast = reactive({
  snapshots: {
    today: null,
    yesterday: null,
    monthToDate: null,
    yearToDate: null,
  },
  generatedAt: {},
  pending: 0,
});
const trendPeriodToSnapshotKey = (value) => {
  switch (String(value || "today").toLowerCase()) {
    case "yesterday":
      return "yesterday";
    case "last30days":
    case "month":
    case "monthtodate":
      return "monthToDate";
    case "year":
    case "yeartodate":
      return "yearToDate";
    default:
      return "today";
  }
};

const publisherTopTags = ref([]);
const publisherTopTagsLoading = ref(false);
const publisherTopTagsError = ref("");
const publisherTopTagsMeta = reactive({
  period: "today",
  startDate: "",
  endDate: "",
  generatedAt: "",
});
const publisherTopTagsPeriod = ref("today");

const publisherTopDomains = ref([]);
const publisherTopDomainsLoading = ref(false);
const publisherTopDomainsError = ref("");
const publisherTopDomainsMeta = reactive({
  period: "today",
  startDate: "",
  endDate: "",
  generatedAt: "",
});
const publisherTopDomainsPeriod = ref("today");

const podcastTopShows = ref([]);
const podcastTopShowsLoading = ref(false);
const podcastTopShowsError = ref("");
const podcastTopShowsMeta = reactive({
  period: "today",
  startDate: "",
  endDate: "",
  generatedAt: "",
});
const podcastTopShowsPeriod = ref("today");

const podcastTopHosting = ref([]);
const podcastTopHostingLoading = ref(false);
const podcastTopHostingError = ref("");
const podcastTopHostingMeta = reactive({
  period: "today",
  startDate: "",
  endDate: "",
  generatedAt: "",
});
const podcastTopHostingPeriod = ref("today");

const publisherTrend = reactive({
  current: [],
  comparison: [],
  granularity: "hour",
  generatedAt: "",
  period: "today",
  currentLabel: "Current",
  comparisonLabel: "Previous",
  currentRange: { start: null, end: null },
  comparisonRange: { start: null, end: null },
});
const publisherTrendLoading = ref(false);
const publisherTrendError = ref("");
const overallTrend = reactive({
  current: [],
  comparison: [],
  granularity: "hour",
  generatedAt: "",
  period: "today",
  currentLabel: "Current",
  comparisonLabel: "Previous",
  currentRange: { start: null, end: null },
  comparisonRange: { start: null, end: null },
});
const overallTrendLoading = ref(false);
const overallTrendError = ref("");

const monthlyTargetsState = reactive({
  plan: null,
});
const monthlyTargetsLoading = ref(false);
const monthlyTargetsError = ref("");

const revenueDialogState = reactive({
  visible: false,
  value: null,
  saving: false,
  error: "",
});

const loadsDialogState = reactive({
  visible: false,
  value: null,
  saving: false,
  error: "",
});

const authHeaders = () => {
  const token = localStorage.getItem("adminToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const formatValue = (value, style = "decimal") =>
  formatMoney(value || 0, style);

const toNumberSafe = (value) => {
  const numeric = Number(value ?? 0);
  return Number.isFinite(numeric) ? numeric : 0;
};

const computeDiffCard = (
  today,
  yesterday,
  label,
  key,
  style = "decimal",
  relative
) => {
  const current = toNumberSafe(today?.[key]);
  const previous = toNumberSafe(yesterday?.[key]);
  const comparisonBase =
    previous !== 0 && previous !== null && previous !== undefined
      ? previous
      : null;
  const diff =
    comparisonBase === null
      ? null
      : ((current - comparisonBase) / Math.abs(comparisonBase)) * 100;

  const relativeValue = relative ? toNumberSafe(relative[key]) : null;
  const runRate =
    relativeValue && relativeValue !== 0
      ? (current / relativeValue) * 100
      : null;
  const deltaPercent = runRate === null ? null : runRate - 100;

  const runRateLabel =
    runRate === null ? "Run Rate N/A" : `${runRate.toFixed(2)}%`;
  const deltaLabel =
    deltaPercent === null ? runRateLabel : `${deltaPercent.toFixed(2)}%`;

  return {
    title: label,
    value: formatValue(current, style),
    delta: deltaLabel,
    isPositive:
      deltaPercent === null
        ? diff === null
          ? true
          : diff >= 0
        : deltaPercent >= 0,
    type: "diff",
  };
};

const buildSnapshotCards = (metrics, spec) =>
  metrics
    ? spec.map(({ key, label, style }) => ({
        title: label,
        value: formatValue(metrics[key], style),
      }))
    : [];

const formatNumber = (value) => Number(value || 0).toLocaleString("en-US");

const formatPercent = (value, digits = 2) =>
  `${(Number(value || 0) * 100).toFixed(digits)}%`;
const formatRatio = (value) => {
  const numeric = Number(value ?? 0);
  if (!Number.isFinite(numeric)) return "0.00";
  return numeric.toFixed(2);
};

const formatPeriodLabel = (value) => {
  switch (String(value || "today").toLowerCase()) {
    case "yesterday":
      return "Yesterday";
    case "monthtodate":
      return "Month To Date";
    case "last30days":
      return "Last 30 Days";
    case "yeartodate":
      return "Year To Date";
    case "weektodate":
      return "Week To Date";
    default:
      return "Today";
  }
};

const currentYearInTimezone = computed(() => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone.value,
    year: "numeric",
  });
  const parsed = Number.parseInt(formatter.format(new Date()), 10);
  return Number.isNaN(parsed) ? new Date().getUTCFullYear() : parsed;
});

const currentMonthIndex = computed(() => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone.value,
    month: "numeric",
  });
  const parsed = Number.parseInt(formatter.format(new Date()), 10);
  return Number.isNaN(parsed) ? new Date().getUTCMonth() : parsed - 1;
});

const currentMonthLabel = computed(() => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone.value,
    month: "long",
    year: "numeric",
  });
  return formatter.format(new Date());
});

const currentDayOfMonth = computed(() => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone.value,
    day: "numeric",
  });
  const parsed = Number.parseInt(formatter.format(new Date()), 10);
  return Number.isNaN(parsed) ? new Date().getUTCDate() : parsed;
});

const daysInCurrentMonth = computed(() => {
  const year = currentYearInTimezone.value;
  const monthIndex = currentMonthIndex.value;
  return new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
});

const elapsedDaysInMonth = computed(() =>
  Math.max(1, Math.min(daysInCurrentMonth.value, currentDayOfMonth.value))
);

const monthlyTargetPlan = computed(() => monthlyTargetsState.plan);

const planTargetMonthIso = computed(() => {
  const planDate = monthlyTargetPlan.value?.targetMonth
    ? new Date(monthlyTargetPlan.value.targetMonth)
    : new Date(
        Date.UTC(currentYearInTimezone.value, currentMonthIndex.value, 1)
      );
  return planDate.toISOString().slice(0, 10);
});

const publisherCardSpec = [
  { key: "requests", label: "Requests" },
  { key: "impressions", label: "Impressions" },
  { key: "requestFillRate", label: "Req Fill Rate", style: "percent" },
  { key: "loads", label: "Loads" },
  { key: "revenue", label: "Revenue", style: "currency" },
  { key: "profit", label: "Profit", style: "currency" },
  { key: "profitMargin", label: "Profit Margin", style: "percent" },
  { key: "adPlayRate", label: "Ad Play Rate", style: "percent" },
];

const publisherSnapshotSpec = [
  { key: "revenue", label: "Revenue", style: "currency" },
  { key: "profit", label: "Profit", style: "currency" },
  { key: "profitMargin", label: "AVG PM", style: "percent" },
  { key: "loads", label: "Loads" },
  { key: "impressions", label: "Impressions" },
  { key: "adPlayRate", label: "Ad play rate", style: "percent" },
  { key: "requests", label: "Requests" },
  { key: "requestFillRate", label: "Req Fill-Rate", style: "percent" },
];

const overallCardLabels = ["Total Revenue", "Total Profit", "Total AVG PM"];

const podcastCardSpec = [
  { key: "downloads", label: "Downloads" },
  { key: "impressions", label: "Impressions" },
  {
    key: "impDownloadRatio",
    label: "Imp/Download Ratio",
  },
  { key: "ecpm", label: "eCPM", style: "currency" },
  { key: "revenue", label: "Revenue", style: "currency" },
  { key: "profit", label: "Profit", style: "currency" },
  { key: "profitMargin", label: "PM", style: "percent" },
  { key: "ctr", label: "CTR", style: "percent" },
];

const podcastSnapshotSpec = [
  { key: "downloads", label: "Downloads" },
  { key: "impressions", label: "Impressions" },
  {
    key: "impDownloadRatio",
    label: "Imp/Download Ratio",
  },
  { key: "ecpm", label: "eCPM", style: "currency" },
  { key: "revenue", label: "Revenue", style: "currency" },
  { key: "profit", label: "Profit", style: "currency" },
  { key: "profitMargin", label: "PM", style: "percent" },
  { key: "ctr", label: "CTR", style: "percent" },
];

const publisherLoading = computed(() => publisher.pending > 0);
const podcastLoading = computed(() => podcast.pending > 0);

const publisherSummaryLoading = computed(() => publisherLoading.value);
const podcastSummaryLoading = computed(() => podcastLoading.value);

const publisherToday = computed(() => publisher.snapshots.today?.metrics);
const publisherTodayRelative = computed(
  () => publisher.snapshots.today?.relative?.metrics
);
const publisherYesterday = computed(
  () => publisher.snapshots.yesterday?.metrics
);
const publisherMonth = computed(() => publisher.snapshots.monthToDate?.metrics);
const publisherYear = computed(() => publisher.snapshots.yearToDate?.metrics);

const podcastToday = computed(() => podcast.snapshots.today?.metrics);
const podcastTodayRelative = computed(
  () => podcast.snapshots.today?.relative?.metrics
);
const podcastYesterday = computed(() => podcast.snapshots.yesterday?.metrics);
const podcastMonth = computed(() => podcast.snapshots.monthToDate?.metrics);
// const podcastYear = computed(() => podcast.snapshots.yearToDate?.metrics);
const overallSnapshot = computed(() => {
  const periodKey = trendPeriodToSnapshotKey(selectedOverallPeriod.value);
  const publisherMetrics = publisher.snapshots[periodKey]?.metrics;
  const podcastMetrics = podcast.snapshots[periodKey]?.metrics;
  if (!publisherMetrics || !podcastMetrics) {
    return null;
  }
  const revenue =
    toNumberSafe(publisherMetrics?.revenue) +
    toNumberSafe(podcastMetrics?.revenue);
  let podcastProfitSource = null;
  if (podcastMetrics?.profit !== undefined && podcastMetrics?.profit !== null) {
    podcastProfitSource = podcastMetrics.profit;
  } else if (
    podcastMetrics?.totalCost !== undefined &&
    podcastMetrics?.totalCost !== null
  ) {
    podcastProfitSource = podcastMetrics.totalCost;
  } else if (podcastMetrics?.gp !== undefined && podcastMetrics?.gp !== null) {
    podcastProfitSource = podcastMetrics.gp;
  }
  const podcastProfit =
    podcastProfitSource !== null
      ? toNumberSafe(podcastProfitSource)
      : toNumberSafe(podcastMetrics?.revenue) -
        toNumberSafe(podcastMetrics?.totalCost);
  const profit = toNumberSafe(publisherMetrics?.profit) + podcastProfit;
  const profitMargin = revenue > 0 ? profit / revenue : 0;
  return { revenue, profit, profitMargin };
});
const overallStatCards = computed(() => {
  if (!overallSnapshot.value) return [];
  return [
    {
      title: overallCardLabels[0],
      value: formatValue(overallSnapshot.value.revenue, "currency"),
    },
    {
      title: overallCardLabels[1],
      value: formatValue(overallSnapshot.value.profit, "currency"),
    },
    {
      title: overallCardLabels[2],
      value: formatPercent(overallSnapshot.value.profitMargin),
    },
  ];
});
const overallSummaryLoading = computed(
  () => publisherLoading.value || podcastLoading.value
);

const publisherCards = computed(() => {
  if (!publisherToday.value || !publisherYesterday.value) return [];
  return publisherCardSpec.map(({ key, label, style }) =>
    computeDiffCard(
      publisherToday.value,
      publisherYesterday.value,
      label,
      key,
      style,
      publisherTodayRelative.value
    )
  );
});

const publisherMonthCards = computed(() =>
  buildSnapshotCards(publisherMonth.value, publisherSnapshotSpec)
);

const publisherYearCards = computed(() =>
  buildSnapshotCards(publisherYear.value, publisherSnapshotSpec)
);

const podcastCards = computed(() => {
  if (!podcastToday.value || !podcastYesterday.value) return [];
  return podcastCardSpec.map(({ key, label, style }) =>
    computeDiffCard(
      podcastToday.value,
      podcastYesterday.value,
      label,
      key,
      style,
      podcastTodayRelative.value
    )
  );
});

const publisherStatCards = computed(() =>
  publisherCards.value.map((card) => ({
    title: card.title,
    contentStr: card.value,
    cardType: card.type,
    subContentStr: card.delta,
    isPlus: card.isPositive,
  }))
);

const podcastStatCards = computed(() =>
  podcastCards.value.map((card) => ({
    title: card.title,
    contentStr: card.value,
    cardType: card.type,
    subContentStr: card.delta,
    isPlus: card.isPositive,
  }))
);

const podcastMonthCards = computed(() =>
  buildSnapshotCards(podcastMonth.value, podcastSnapshotSpec)
);

// const podcastYearCards = computed(() =>
//   buildSnapshotCards(podcastYear.value, podcastSnapshotSpec)
// );

const publisherMonthBusy = computed(
  () => publisherLoading.value && !publisherMonthCards.value.length
);
const publisherYearBusy = computed(
  () => publisherLoading.value && !publisherYearCards.value.length
);
const podcastMonthBusy = computed(
  () => podcastLoading.value && !podcastMonthCards.value.length
);
// const podcastYearBusy = computed(
//   () => podcastLoading.value && !podcastYearCards.value.length
// );
const podcastRunRateLabels = [
  "Monthly Revenue Run Rate",
  "Daily Revenue Avg",
  "Monthly Cost Run Rate",
  "Daily Cost Avg",
  "Monthly Downloads Run Rate",
  "Daily Downloads Avg",
  "Monthly Impressions Run Rate",
  "CTR",
];

const podcastRunRateCards = computed(() => {
  const monthMetrics = podcastMonth.value;
  if (!monthMetrics) return [];

  const elapsedDays = Math.max(1, elapsedDaysInMonth.value);
  const daysInMonth = Math.max(1, daysInCurrentMonth.value);

  const toNumber = (value) => {
    const numeric = Number(value || 0);
    return Number.isFinite(numeric) ? numeric : 0;
  };

  const revenue = toNumber(monthMetrics.revenue);
  const totalCost = toNumber(monthMetrics.totalCost);
  const downloads = toNumber(monthMetrics.downloads);
  const impressions = toNumber(monthMetrics.impressions);

  const averageRevenue = elapsedDays > 0 ? revenue / elapsedDays : 0;
  const averageCost = elapsedDays > 0 ? totalCost / elapsedDays : 0;
  const averageDownloads = elapsedDays > 0 ? downloads / elapsedDays : 0;
  const averageImpressions = elapsedDays > 0 ? impressions / elapsedDays : 0;

  const projectedRevenue = averageRevenue * daysInMonth;
  const projectedCost = averageCost * daysInMonth;
  const projectedDownloads = averageDownloads * daysInMonth;
  const projectedImpressions = averageImpressions * daysInMonth;

  return [
    {
      title: "Monthly Revenue Run Rate",
      value: formatValue(projectedRevenue, "currency"),
    },
    {
      title: "Daily Revenue Avg",
      value: formatValue(averageRevenue, "currency"),
    },
    {
      title: "Monthly Cost Run Rate",
      value: formatValue(projectedCost, "currency"),
    },
    {
      title: "Daily Cost Avg",
      value: formatValue(averageCost, "currency"),
    },
    {
      title: "Monthly Downloads Run Rate",
      value: formatNumber(projectedDownloads),
    },
    {
      title: "Daily Downloads Avg",
      value: formatNumber(averageDownloads),
    },
    {
      title: "Monthly Impressions Run Rate",
      value: formatNumber(projectedImpressions),
    },
    {
      title: "CTR",
      value: formatPercent(monthMetrics.ctr ?? 0),
    },
  ];
});

const podcastRunRateLoading = computed(
  () => podcastLoading.value && !podcastRunRateCards.value.length
);

const podcastDetailSections = computed(() => [
  {
    id: "podcast-run-rate",
    title: "Run Rate",
    cards: podcastRunRateCards.value,
    loading: podcastRunRateLoading.value,
    loadingMessage: "Calculating podcast run rate...",
    emptyMessage: "Run-rate not available.",
    placeholderLabels: podcastRunRateLabels,
  },
]);

const publisherTrendDatasets = buildTrendDatasets(
  publisherTrend,
  selectedTrendMetric
);
const overallTrendDatasets = buildTrendDatasets(
  overallTrend,
  selectedOverallMetric
);

const publisherRunRate = computed(() => {
  const month = publisher.snapshots.monthToDate;
  const context = month?.context;
  const metrics = month?.metrics;
  const target = monthlyTargetPlan.value;
  if (!metrics || !context) return [];

  const { daysInMonth, elapsedDays } = context;
  const safeDays = daysInMonth || 0;
  const averageRevenue = elapsedDays > 0 ? metrics.revenue / elapsedDays : 0;
  const averageLoads = elapsedDays > 0 ? metrics.loads / elapsedDays : 0;

  const monthlyRevenuePlanValue = target?.monthlyRev ?? null;
  const monthlyLoadsPlanValue = target?.monthlyLoads ?? null;

  const dailyRevenuePlanValue =
    monthlyRevenuePlanValue !== null && safeDays > 0
      ? monthlyRevenuePlanValue / safeDays
      : null;
  const dailyLoadsPlanValue =
    monthlyLoadsPlanValue !== null && safeDays > 0
      ? monthlyLoadsPlanValue / safeDays
      : null;

  return [
    {
      key: "monthly-revenue",
      label: "Monthly REV",
      planned:
        monthlyRevenuePlanValue !== null
          ? formatValue(monthlyRevenuePlanValue, "currency")
          : "-",
      rate: formatValue(averageRevenue * safeDays, "currency"),
      canEdit: true,
      editType: "revenue",
    },
    {
      key: "daily-revenue",
      label: "Daily REV",
      planned:
        dailyRevenuePlanValue !== null
          ? formatValue(dailyRevenuePlanValue, "currency")
          : "-",
      rate: formatValue(averageRevenue, "currency"),
      canEdit: false,
    },
    {
      key: "monthly-loads",
      label: "Monthly Loads",
      planned:
        monthlyLoadsPlanValue !== null
          ? formatValue(monthlyLoadsPlanValue)
          : "-",
      rate: formatValue(averageLoads * safeDays),
      canEdit: true,
      editType: "loads",
    },
    {
      key: "daily-loads",
      label: "Daily Loads",
      planned:
        dailyLoadsPlanValue !== null ? formatValue(dailyLoadsPlanValue) : "-",
      rate: formatValue(averageLoads),
      canEdit: false,
    },
  ];
});

const publisherRunRateSection = computed(() => ({
  rows: publisherRunRate.value,
  loading:
    (publisherLoading.value && !publisherRunRate.value.length) ||
    monthlyTargetsLoading.value,
  editable: true,
  onEdit: (row) => {
    if (row?.editType === "revenue") {
      openRevenueDialog();
      return;
    }
    if (row?.editType === "loads") {
      openLoadsDialog();
    }
  },
}));

const selectedTrendMetricOption = computed(
  () =>
    trendMetricOptions.find(
      (option) => option.value === selectedTrendMetric.value
    ) || trendMetricOptions[0]
);

const trendChartPrefix = computed(
  () => selectedTrendMetricOption.value?.prefix || ""
);

const trendChartSuffix = computed(
  () => selectedTrendMetricOption.value?.suffix || ""
);

const trendMetricLabel = computed(
  () => selectedTrendMetricOption.value?.label || "Metric"
);
const selectedOverallMetricOption = computed(
  () =>
    trendMetricOptions.find(
      (option) => option.value === selectedOverallMetric.value
    ) || trendMetricOptions[0]
);
const overallChartPrefix = computed(
  () => selectedOverallMetricOption.value?.prefix || ""
);
const overallChartSuffix = computed(
  () => selectedOverallMetricOption.value?.suffix || ""
);
const overallTrendMetricLabel = computed(
  () => selectedOverallMetricOption.value?.label || "Metric"
);

const openRevenueDialog = () => {
  const plan = monthlyTargetPlan.value;
  const existingValue =
    plan?.monthlyRev !== null && plan?.monthlyRev !== undefined
      ? Number(plan.monthlyRev)
      : null;
  revenueDialogState.value =
    existingValue !== null && Number.isFinite(existingValue)
      ? existingValue
      : null;
  revenueDialogState.error = "";
  revenueDialogState.visible = true;
};

const openLoadsDialog = () => {
  const plan = monthlyTargetPlan.value;
  const existingValue =
    plan?.monthlyLoads !== null && plan?.monthlyLoads !== undefined
      ? Number(plan.monthlyLoads)
      : null;
  loadsDialogState.value =
    existingValue !== null && Number.isFinite(existingValue)
      ? existingValue
      : null;
  loadsDialogState.error = "";
  loadsDialogState.visible = true;
};

const closeRevenueDialog = () => {
  revenueDialogState.visible = false;
  revenueDialogState.saving = false;
};

const closeLoadsDialog = () => {
  loadsDialogState.visible = false;
  loadsDialogState.saving = false;
};

const submitRevenueUpdate = async () => {
  if (revenueDialogState.saving) return;
  revenueDialogState.error = "";
  revenueDialogState.saving = true;

  try {
    const payload = {
      targetMonth: planTargetMonthIso.value,
      monthlyRev: revenueDialogState.value,
      monthlyLoads: monthlyTargetPlan.value?.monthlyLoads,
    };

    const { data } = await axios.put(
      `${API_BASE_URL}/management-dashboard/monthly-targets`,
      payload,
      {
        headers: authHeaders(),
      }
    );

    if (data?.success) {
      closeRevenueDialog();
      await fetchMonthlyTargets();
    } else {
      const message =
        data?.message || "Unable to update monthly revenue target.";
      revenueDialogState.error = message;
      toast.add({
        severity: "error",
        summary: "Monthly Target",
        detail: message,
        life: 5000,
      });
    }
  } catch (error) {
    console.error("Error updating monthly revenue target", error);
    const message =
      error?.response?.data?.message ||
      "Unable to update monthly revenue target.";
    revenueDialogState.error = message;
    toast.add({
      severity: "error",
      summary: "Monthly Target",
      detail: message,
      life: 5000,
    });
  } finally {
    revenueDialogState.saving = false;
  }
};

const submitLoadsUpdate = async () => {
  if (loadsDialogState.saving) return;
  loadsDialogState.error = "";
  loadsDialogState.saving = true;

  try {
    const payload = {
      targetMonth: planTargetMonthIso.value,
      monthlyLoads: loadsDialogState.value,
      monthlyRev: monthlyTargetPlan.value?.monthlyRev,
    };

    const { data } = await axios.put(
      `${API_BASE_URL}/management-dashboard/monthly-targets`,
      payload,
      {
        headers: authHeaders(),
      }
    );

    if (data?.success) {
      closeLoadsDialog();
      await fetchMonthlyTargets();
    } else {
      const message = data?.message || "Unable to update monthly loads target.";
      loadsDialogState.error = message;
      toast.add({
        severity: "error",
        summary: "Monthly Target",
        detail: message,
        life: 5000,
      });
    }
  } catch (error) {
    console.error("Error updating monthly loads target", error);
    const message =
      error?.response?.data?.message ||
      "Unable to update monthly loads target.";
    loadsDialogState.error = message;
    toast.add({
      severity: "error",
      summary: "Monthly Target",
      detail: message,
      life: 5000,
    });
  } finally {
    loadsDialogState.saving = false;
  }
};

const handleTrendMetricChange = (value) => {
  if (value === selectedTrendMetric.value) return;
  selectedTrendMetric.value = value;
};

const handleTrendPeriodChange = async (value) => {
  if (value === selectedTrendPeriod.value) return;
  selectedTrendPeriod.value = value;
  await fetchPublisherTrend();
};
const handleOverallMetricChange = (value) => {
  if (value === selectedOverallMetric.value) return;
  selectedOverallMetric.value = value;
};
const handleOverallPeriodChange = async (value) => {
  if (value === selectedOverallPeriod.value) return;
  selectedOverallPeriod.value = value;
  await fetchOverallTrend();
};

const publisherTopTagsColumns = [
  {
    field: "partner",
    label: "Demand Partner",
    sortable: true,
    defaultSortOrder: "desc",
  },
  {
    field: "impressions",
    label: "Impressions",
    sortable: true,
    defaultSortOrder: "desc",
  },
  { field: "requests", label: "Requests", sortable: true },
  { field: "fillRate", label: "Fill Rate", sortable: true },
  { field: "revenue", label: "Revenue", sortable: true },
  { field: "profitMargin", label: "Profit Margin", sortable: true },
];

const publisherTopDomainsColumns = [
  { field: "domain", label: "Domain", sortable: true },
  { field: "group", label: "Group", sortable: true },
  {
    field: "loads",
    label: "Loads",
    sortable: true,
    defaultSortOrder: "desc",
  },
  { field: "impressions", label: "Impressions", sortable: true },
  { field: "requests", label: "Requests", sortable: true },
  { field: "adPlayRate", label: "Ad Play Rate", sortable: true },
  { field: "fillRate", label: "Fill Rate", sortable: true },
  { field: "revenue", label: "Revenue", sortable: true },
  { field: "cost", label: "Cost", sortable: true },
  { field: "profit", label: "Profit", sortable: true },
  { field: "profitMargin", label: "Profit Margin", sortable: true },
  { field: "cpl", label: "CPL", sortable: true },
  { field: "rpl", label: "RPL", sortable: true },
];

const publisherTopTagsRows = computed(() =>
  publisherTopTags.value.map((tag, index) => ({
    __key: `${tag.tagId || "unknown"}-${index}`,
    partner: tag.partner || "Unknown",
    impressions: formatNumber(tag.impressions),
    requests: formatNumber(tag.requests),
    fillRate: formatPercent(tag.requestFillRate, 2),
    revenue: formatValue(tag.revenue, "currency"),
    profitMargin: formatPercent(tag.profitMargin),
  }))
);

const publisherTopDomainsRows = computed(() =>
  publisherTopDomains.value.map((domain, index) => ({
    __key: `${domain.domain || "unknown"}-${index}`,
    domain: domain.domain || "Unknown",
    group: domain.groupName || "Unknown",
    loads: formatNumber(domain.loads),
    impressions: formatNumber(domain.impressions),
    requests: formatNumber(domain.requests),
    adPlayRate: formatPercent(domain.adPlayRate, 2),
    fillRate: formatPercent(domain.requestFillRate, 2),
    revenue: formatValue(domain.revenue, "currency"),
    cost: formatValue(domain.cost, "currency"),
    profit: formatValue(domain.profit, "currency"),
    profitMargin: formatPercent(domain.profitMargin),
    cpl: formatValue(domain.cpl, "currency"),
    rpl: formatValue(domain.rpl, "currency"),
  }))
);

const podcastTopShowsColumns = [
  { field: "title", label: "Show Title", sortable: true },
  { field: "views", label: "Views", sortable: true },
  { field: "sessions", label: "Sessions", sortable: true },
  {
    field: "downloads",
    label: "Downloads",
    sortable: true,
    defaultSortOrder: "desc",
  },
  { field: "revenue", label: "Revenue", sortable: true },
  { field: "cost", label: "Cost", sortable: true },
  { field: "ctr", label: "CTR", sortable: true },
  { field: "impressions", label: "Impressions", sortable: true },
  { field: "uniqueListeners", label: "Unique Listeners", sortable: true },
];

const podcastTopHostingColumns = [
  { field: "hosting_platform", label: "Hosting Platform", sortable: true },
  {
    field: "downloads",
    label: "Downloads",
    sortable: true,
    defaultSortOrder: "desc",
  },
  { field: "revenue", label: "Revenue", sortable: true },
  { field: "cost", label: "Cost", sortable: true },
  { field: "impressions", label: "Impressions", sortable: true },
  {
    field: "impDownloadRatio",
    label: "Impressions / Downloads Ratio",
    sortable: true,
  },
];

const podcastTopShowsRows = computed(() =>
  podcastTopShows.value.map((show, index) => ({
    __key: `${show.show_id || show.title || "show"}-${index}`,
    title: show.title || "Unknown",
    views: formatNumber(show.views),
    sessions: formatNumber(show.sessions),
    downloads: formatNumber(show.downloads),
    revenue: formatValue(show.revenue, "currency"),
    cost: formatValue(show.cost, "currency"),
    ctr: formatPercent(show.ctr, 2),
    impressions: formatNumber(show.impressions),
    uniqueListeners: formatNumber(show.uniqueListeners),
  }))
);

const podcastTopHostingRows = computed(() =>
  podcastTopHosting.value.map((host, index) => ({
    __key: `${host.hosting_platform || "platform"}-${index}`,
    hosting_platform: host.hosting_platform || "Unknown",
    downloads: formatNumber(host.downloads),
    revenue: formatValue(host.revenue, "currency"),
    cost: formatValue(host.cost, "currency"),
    impressions: formatNumber(host.impressions),
    impDownloadRatio:
      host.downloads > 0
        ? formatRatio(host.impressions / host.downloads)
        : "0.00",
  }))
);

const publisherTables = computed(() => [
  {
    id: "publisher-top-tags",
    title: "Top Demand Tags",
    subtitle: formatPeriodLabel(publisherTopTagsMeta.period),
    loading: publisherTopTagsLoading.value,
    error: publisherTopTagsError.value,
    loadingMessage: "Loading top demand tags...",
    emptyMessage: "No tag data for this range.",
    columns: publisherTopTagsColumns,
    rows: publisherTopTagsRows.value,
    periodOptions: tablePeriodOptions,
    selectedPeriod: publisherTopTagsPeriod.value,
    filterable: true,
    filterPlaceholder: "Filter partners…",
    defaultSortField: "impressions",
    onPeriodChange: handlePublisherTopTagsPeriodChange,
  },
  {
    id: "publisher-top-domains",
    title: "Top Domains",
    subtitle: formatPeriodLabel(publisherTopDomainsMeta.period),
    loading: publisherTopDomainsLoading.value,
    error: publisherTopDomainsError.value,
    loadingMessage: "Loading top domains...",
    emptyMessage: "No domain data for this range.",
    columns: publisherTopDomainsColumns,
    rows: publisherTopDomainsRows.value,
    periodOptions: tablePeriodOptions,
    selectedPeriod: publisherTopDomainsPeriod.value,
    filterable: true,
    filterPlaceholder: "Filter domains…",
    defaultSortField: "loads",
    onPeriodChange: handlePublisherTopDomainsPeriodChange,
  },
]);

const podcastTables = computed(() => [
  {
    id: "podcast-top-shows",
    title: "Top Shows",
    subtitle: formatPeriodLabel(podcastTopShowsMeta.period),
    loading: podcastTopShowsLoading.value,
    error: podcastTopShowsError.value,
    loadingMessage: "Loading top shows...",
    emptyMessage: "No show data for this range.",
    columns: podcastTopShowsColumns,
    rows: podcastTopShowsRows.value,
    periodOptions: tablePeriodOptions,
    selectedPeriod: podcastTopShowsPeriod.value,
    filterable: true,
    filterPlaceholder: "Filter shows…",
    defaultSortField: "downloads",
    onPeriodChange: (period) => handlePodcastTopShowsPeriodChange(period),
  },
  {
    id: "podcast-top-hosting",
    title: "Top Hosting Platforms",
    subtitle: formatPeriodLabel(podcastTopHostingMeta.period),
    loading: podcastTopHostingLoading.value,
    error: podcastTopHostingError.value,
    loadingMessage: "Loading hosting stats...",
    emptyMessage: "No hosting data for this range.",
    columns: podcastTopHostingColumns,
    rows: podcastTopHostingRows.value,
    periodOptions: tablePeriodOptions,
    selectedPeriod: podcastTopHostingPeriod.value,
    filterable: true,
    filterPlaceholder: "Filter hosting platforms…",
    defaultSortField: "downloads",
    onPeriodChange: (period) => handlePodcastTopHostingPeriodChange(period),
  },
]);

const isRefreshing = computed(
  () =>
    publisherLoading.value ||
    podcastLoading.value ||
    publisherTrendLoading.value ||
    overallTrendLoading.value ||
    publisherTopTagsLoading.value ||
    publisherTopDomainsLoading.value ||
    podcastTopShowsLoading.value ||
    podcastTopHostingLoading.value ||
    monthlyTargetsLoading.value
);

const fetchManagementDashboard = async (params) => {
  const { data } = await axios.get(`${API_BASE_URL}/management-dashboard`, {
    params,
    headers: authHeaders(),
    // timeout: 60000,
  });
  return data;
};

const fetchMonthlyTargets = async () => {
  monthlyTargetsLoading.value = true;
  monthlyTargetsError.value = "";

  try {
    const { data } = await axios.get(
      `${API_BASE_URL}/management-dashboard/monthly-targets`,
      {
        headers: authHeaders(),
      }
    );

    if (data?.success) {
      const payload = data.data || {};
      const toNumberOrNull = (raw) => {
        if (raw === null || raw === undefined || raw === "") return null;
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : null;
      };
      monthlyTargetsState.plan = {
        targetId: payload?.targetId ?? null,
        targetMonth: payload?.targetMonth ?? null,
        monthlyRev: toNumberOrNull(payload?.monthlyRev),
        monthlyLoads: toNumberOrNull(payload?.monthlyLoads),
        createdAt: payload?.createdAt ?? null,
        updatedAt: payload?.updatedAt ?? null,
      };
    } else {
      monthlyTargetsError.value =
        data?.message || "Unable to load monthly targets.";
      monthlyTargetsState.plan = null;
    }
  } catch (error) {
    console.error("Error fetching monthly targets", error);
    monthlyTargetsError.value =
      error?.response?.data?.message || "Unable to load monthly targets.";
    monthlyTargetsState.plan = null;
  } finally {
    monthlyTargetsLoading.value = false;
  }
};

const fetchPublisherPeriod = async (period) => {
  publisher.pending += 1;
  try {
    const data = await fetchManagementDashboard({
      section: "publisher",
      period,
      timezone: timezone.value,
    });
    if (data?.success) {
      publisher.snapshots[period] = data.data;
      publisher.generatedAt[period] = data.data?.generatedAt;
      if (period === "monthToDate" && data.data?.context) {
        publisher.context.monthToDate = data.data.context;
      }
    } else {
      publisher.snapshots[period] = null;
    }
  } catch (error) {
    console.error("Error fetching publisher period", period, error);
    publisher.snapshots[period] = null;
  } finally {
    publisher.pending -= 1;
  }
};

const fetchPodcastPeriod = async (period) => {
  podcast.pending += 1;
  try {
    const data = await fetchManagementDashboard({
      section: "podcast",
      period,
      timezone: timezone.value,
    });
    if (data?.success) {
      podcast.snapshots[period] = data.data;
      podcast.generatedAt[period] = data.data?.generatedAt;
    } else {
      podcast.snapshots[period] = null;
    }
  } catch (error) {
    console.error("Error fetching podcast period", period, error);
    podcast.snapshots[period] = null;
  } finally {
    podcast.pending -= 1;
  }
};

const tryDatasetWithFallback = async ({
  target,
  meta,
  loadingRef,
  errorRef,
  section,
  dataset,
  periodsToTry,
  emptyValueKey,
}) => {
  loadingRef.value = true;
  errorRef.value = "";
  try {
    const attempts = periodsToTry;

    for (let i = 0; i < attempts.length; i += 1) {
      const attemptPeriod = attempts[i];
      try {
        const data = await fetchManagementDashboard({
          section,
          dataset,
          period: attemptPeriod,
          timezone: timezone.value,
        });

        if (!data?.success) {
          errorRef.value = data?.message || "Unable to load data.";
          target.value = [];
          return;
        }

        const payload = data.data || {};
        const rows = payload[emptyValueKey] || [];

        target.value = rows;
        meta.period = payload.period || attemptPeriod;
        meta.startDate = payload.startDate || "";
        meta.endDate = payload.endDate || "";
        meta.generatedAt = payload.generatedAt || new Date().toISOString();

        if (rows.length || i === attempts.length - 1) {
          return;
        }
        // Continue to next attempt if rows empty.
      } catch (error) {
        console.error(
          `Error fetching ${section} ${dataset} for ${attemptPeriod}`,
          error
        );
        errorRef.value =
          error?.response?.data?.message || "Unable to load data.";
        target.value = [];
        return;
      }
    }
  } finally {
    loadingRef.value = false;
  }
};

const fetchPublisherTrend = async () => {
  publisherTrendLoading.value = true;
  publisherTrendError.value = "";
  try {
    const data = await fetchManagementDashboard({
      section: "publisher",
      dataset: "trend",
      period: selectedTrendPeriod.value,
      timezone: timezone.value,
    });
    if (data?.success) {
      const payload = data.data || {};
      publisherTrend.current = Array.isArray(payload.current)
        ? payload.current
        : [];
      publisherTrend.comparison = Array.isArray(payload.comparison)
        ? payload.comparison
        : [];
      publisherTrend.generatedAt =
        payload.generatedAt || new Date().toISOString();
      publisherTrend.period = payload.period || selectedTrendPeriod.value;
      publisherTrend.granularity = payload.granularity || "hour";
      publisherTrend.currentLabel = payload.currentLabel || "Current";
      publisherTrend.comparisonLabel = payload.comparisonLabel || "Previous";
      publisherTrend.currentRange = payload.currentRange || {
        start: null,
        end: null,
      };
      publisherTrend.comparisonRange = payload.comparisonRange || {
        start: null,
        end: null,
      };
    } else {
      publisherTrend.current = [];
      publisherTrend.comparison = [];
      publisherTrend.generatedAt = "";
      publisherTrendError.value = data?.message || "Unable to load trend data.";
    }
  } catch (error) {
    console.error("Error fetching publisher trend", error);
    publisherTrend.current = [];
    publisherTrend.comparison = [];
    publisherTrend.generatedAt = "";
    publisherTrendError.value =
      error?.response?.data?.message || "Unable to load trend data.";
  } finally {
    publisherTrendLoading.value = false;
  }
};
const fetchOverallTrend = async () => {
  overallTrendLoading.value = true;
  overallTrendError.value = "";
  try {
    const data = await fetchManagementDashboard({
      section: "overall",
      dataset: "trend",
      period: selectedOverallPeriod.value,
      timezone: timezone.value,
    });
    if (data?.success) {
      const payload = data.data || {};
      overallTrend.current = Array.isArray(payload.current)
        ? payload.current
        : [];
      overallTrend.comparison = Array.isArray(payload.comparison)
        ? payload.comparison
        : [];
      overallTrend.generatedAt =
        payload.generatedAt || new Date().toISOString();
      overallTrend.period = payload.period || selectedOverallPeriod.value;
      overallTrend.granularity = payload.granularity || "hour";
      overallTrend.currentLabel = payload.currentLabel || "Current";
      overallTrend.comparisonLabel = payload.comparisonLabel || "Previous";
      overallTrend.currentRange = payload.currentRange || {
        start: null,
        end: null,
      };
      overallTrend.comparisonRange = payload.comparisonRange || {
        start: null,
        end: null,
      };
    } else {
      overallTrend.current = [];
      overallTrend.comparison = [];
      overallTrend.generatedAt = "";
      overallTrendError.value = data?.message || "Unable to load trend data.";
    }
  } catch (error) {
    console.error("Error fetching overall trend", error);
    overallTrend.current = [];
    overallTrend.comparison = [];
    overallTrend.generatedAt = "";
    overallTrendError.value =
      error?.response?.data?.message || "Unable to load trend data.";
  } finally {
    overallTrendLoading.value = false;
  }
};
const fetchPublisherTopTags = async () => {
  await tryDatasetWithFallback({
    target: publisherTopTags,
    meta: publisherTopTagsMeta,
    loadingRef: publisherTopTagsLoading,
    errorRef: publisherTopTagsError,
    section: "publisher",
    dataset: "toptags",
    periodsToTry: [publisherTopTagsPeriod.value, "monthToDate"].filter(
      (period, index, arr) => arr.indexOf(period) === index
    ),
    emptyValueKey: "tags",
  });
};

const fetchPublisherTopDomains = async () => {
  await tryDatasetWithFallback({
    target: publisherTopDomains,
    meta: publisherTopDomainsMeta,
    loadingRef: publisherTopDomainsLoading,
    errorRef: publisherTopDomainsError,
    section: "publisher",
    dataset: "topdomains",
    periodsToTry: [publisherTopDomainsPeriod.value, "monthToDate"].filter(
      (period, index, arr) => arr.indexOf(period) === index
    ),
    emptyValueKey: "domains",
  });
};

const handlePublisherTopTagsPeriodChange = async (value) => {
  if (publisherTopTagsPeriod.value === value) return;
  publisherTopTagsPeriod.value = value;
  await fetchPublisherTopTags();
};

const handlePublisherTopDomainsPeriodChange = async (value) => {
  if (publisherTopDomainsPeriod.value === value) return;
  publisherTopDomainsPeriod.value = value;
  await fetchPublisherTopDomains();
};

const handlePodcastTopShowsPeriodChange = async (value) => {
  if (podcastTopShowsPeriod.value === value) return;
  podcastTopShowsPeriod.value = value;
  await fetchPodcastTopShows();
};

const handlePodcastTopHostingPeriodChange = async (value) => {
  if (podcastTopHostingPeriod.value === value) return;
  podcastTopHostingPeriod.value = value;
  await fetchPodcastTopHosting();
};

const fetchPodcastTopShows = async () => {
  await tryDatasetWithFallback({
    target: podcastTopShows,
    meta: podcastTopShowsMeta,
    loadingRef: podcastTopShowsLoading,
    errorRef: podcastTopShowsError,
    section: "podcast",
    dataset: "topshows",
    periodsToTry: [podcastTopShowsPeriod.value, "monthToDate"].filter(
      (period, index, arr) => arr.indexOf(period) === index
    ),
    emptyValueKey: "shows",
  });
};

const fetchPodcastTopHosting = async () => {
  await tryDatasetWithFallback({
    target: podcastTopHosting,
    meta: podcastTopHostingMeta,
    loadingRef: podcastTopHostingLoading,
    errorRef: podcastTopHostingError,
    section: "podcast",
    dataset: "tophosting",
    periodsToTry: [podcastTopHostingPeriod.value, "monthToDate"].filter(
      (period, index, arr) => arr.indexOf(period) === index
    ),
    emptyValueKey: "hosting",
  });
};

const refreshAll = async () => {
  if (isRefreshing.value) return;

  const runSafe = (promiseFactory) =>
    promiseFactory().catch((error) => {
      console.error("Dashboard refresh error:", error);
    });

  await Promise.all([
    ...publisherPeriods.map((period) =>
      runSafe(() => fetchPublisherPeriod(period))
    ),
    ...podcastPeriods.map((period) =>
      runSafe(() => fetchPodcastPeriod(period))
    ),
    // runSafe(() => fetchPodcastPeriod("yearToDate")), // disabled: pending DB fix
    runSafe(fetchMonthlyTargets),
    runSafe(fetchPublisherTrend),
    runSafe(fetchOverallTrend),
    runSafe(fetchPublisherTopTags),
    runSafe(fetchPublisherTopDomains),
    runSafe(fetchPodcastTopShows),
    runSafe(fetchPodcastTopHosting),
  ]);
};

watch(
  () => timezone.value,
  () => {
    refreshAll();
  },
  { immediate: true }
);
</script>

<style>
.md-section-heading {
  font-size: 1.4rem;
  font-weight: 600;
  margin: 1.5rem 0 0.5rem;
  color: var(--text-color);
}

.md-subheading {
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-color-secondary);
  margin-bottom: 0.75rem;
}

.md-empty {
  border: 1px dashed var(--surface-border);
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  font-size: 0.9rem;
  color: var(--text-color-secondary);
  background: var(--surface-ground);
}

.table-responsive {
  overflow-x: auto;
}

.md-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.md-table thead {
  background: var(--surface-100);
}

.md-table th,
.md-table td {
  padding: 0.75rem;
  border-bottom: 1px solid var(--surface-border);
  text-align: left;
}
</style>
